import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ALARM_SOUNDS,
  DAY_LABELS,
  OFF_AFTER_STEPS,
  STANDBY_OPTIONS,
  alarmSummary,
  formatDays,
  formatSeconds,
  loadDeviceSettings,
  newId,
  nextAlarm,
  quietSummary,
  saveDeviceSettings,
  soundName,
  standbySummary,
  toMinutes,
  todayIndex,
} from "./deviceSettingsModel";
import {
  AddButton,
  DS,
  DangerButton,
  DayChips,
  DayRing,
  Dialog,
  Label,
  PlayButton,
  RoundPreview,
  SERIF,
  TimeInput,
  Toggle,
  useMinuteClock,
  useSoundPreview,
} from "./DeviceSettingsUi";

/**
 * "Gerät" block on the patient profile: rest periods, alarms, the standby
 * screen and the wake word switch. Every change is saved right away; a failed
 * save restores the last saved version.
 */
export default function DeviceSettingsSection({ patientId }) {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const saved = useRef(null);
  const latest = useRef(null);
  const queue = useRef(Promise.resolve());

  useEffect(() => {
    let ignore = false;
    loadDeviceSettings(patientId)
      .then((data) => {
        if (ignore) return;
        saved.current = data;
        latest.current = data;
        setSettings(data);
      })
      .catch((e) => !ignore && setError(e.message));
    return () => { ignore = true; };
  }, [patientId]);

  const update = useCallback((change) => {
    if (!latest.current) return;
    const next = change(latest.current);
    latest.current = next;
    setSettings(next);
    setError("");
    queue.current = queue.current.then(async () => {
      try {
        saved.current = await saveDeviceSettings(patientId, next);
      } catch (e) {
        latest.current = saved.current;
        setSettings(saved.current);
        setError(e.message);
      }
    });
  }, [patientId]);

  const rows = settings ? [
    { id: "quiet", title: "Ruhezeiten", sub: quietSummary(settings.quiet), icon: "moon", dark: settings.quiet.enabled },
    { id: "alarms", title: "Wecker", sub: alarmSummary(settings.alarms), icon: "alarm" },
    { id: "standby", title: "Standby-Bildschirm", sub: standbySummary(settings.standby), icon: "screen" },
  ] : [];
  // Unset follows the device's default, which ships switched on.
  const wakeword = settings?.wakeword !== false;

  return (
    <section>
      <h2 className="mb-2 text-xl font-semibold text-gray-900">Gerät</h2>
      <div className="mb-3 h-px w-full bg-gray-300/70" />
      {error && <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      {!settings && !error && <div className="text-sm text-gray-700">Geräteeinstellungen werden geladen…</div>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => setOpen(row.id)}
            className="flex items-center gap-3 rounded-lg border border-black/10 bg-blue-50 p-3 text-left hover:bg-blue-100/70"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: row.dark ? DS.night : DS.accentSoft, color: row.dark ? DS.nightInk : DS.accentInk }}>
              <RowIcon name={row.icon} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-lg font-semibold text-gray-900">{row.title}</span>
              <span className="block truncate text-sm text-gray-600">{row.sub}</span>
            </span>
          </button>
        ))}
        {/* No dialog of its own: the switch is the whole setting. */}
        {settings && (
          <div className="flex items-center gap-3 rounded-lg border border-black/10 bg-blue-50 p-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: wakeword ? DS.accent : DS.accentSoft, color: wakeword ? "#fff" : DS.accentInk }}>
              <RowIcon name="mic" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-lg font-semibold text-gray-900">„Hey Steffi“</span>
              <span className="block text-sm text-gray-600">Gespräch per Zuruf starten</span>
            </span>
            <Toggle on={wakeword} onChange={(on) => update((s) => ({ ...s, wakeword: on }))} label="Hey Steffi" />
          </div>
        )}
      </div>

      {settings && open === "quiet" && <QuietDialog settings={settings} update={update} onClose={() => setOpen(null)} />}
      {settings && open === "alarms" && <AlarmsDialog settings={settings} update={update} onClose={() => setOpen(null)} />}
      {settings && open === "standby" && <StandbyDialog settings={settings} update={update} onClose={() => setOpen(null)} />}
    </section>
  );
}

function RowIcon({ name }) {
  const p = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  if (name === "moon") return <svg {...p}><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /></svg>;
  if (name === "alarm") return <svg {...p}><circle cx="12" cy="13" r="7" /><polyline points="12 9.5 12 13 14.5 14.5" /><path d="M4 5l3-2M20 5l-3-2" /></svg>;
  if (name === "mic") return <svg {...p}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></svg>;
  return <svg {...p}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /></svg>;
}

// ─── Ruhezeiten ─────────────────────────────────────────────────────
function QuietDialog({ settings, update, onClose }) {
  const q = settings.quiet;
  const now = useMinuteClock();
  const [day, setDay] = useState(todayIndex());
  const [edit, setEdit] = useState(null);
  const set = (patch) => update((s) => ({ ...s, quiet: { ...s.quiet, ...patch } }));
  const dayWindows = q.windows.filter((w) => w.days.includes(day));

  const saveWindow = () => {
    if (!edit.days.length) return window.alert("Bitte mindestens einen Wochentag auswählen.");
    if (edit.from === edit.to) return window.alert("Beginn und Ende müssen sich unterscheiden.");
    const { isNew, ...win } = edit;
    set({ windows: q.windows.some((w) => w.id === win.id) ? q.windows.map((w) => (w.id === win.id ? win : w)) : [...q.windows, win] });
    return setEdit(null);
  };

  if (edit) {
    const from = toMinutes(edit.from);
    const to = toMinutes(edit.to);
    const hours = (((to - from + 1440) % 1440) || 1440) / 60;
    return (
      <Dialog open title="Zeitfenster" onCancel={() => setEdit(null)} onSave={saveWindow}>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-2">Von</Label><TimeInput value={edit.from} onChange={(v) => setEdit({ ...edit, from: v })} /></div>
          <div><Label className="mb-2">Bis</Label><TimeInput value={edit.to} onChange={(v) => setEdit({ ...edit, to: v })} /></div>
        </div>
        <div className="text-center text-sm" style={{ color: DS.muted }}>
          {to < from ? "Über Nacht · " : ""}{hours.toLocaleString("de-DE", { maximumFractionDigits: 1 })} Stunden Ruhe
        </div>
        <div><Label className="mb-2.5">An diesen Tagen</Label><DayChips days={edit.days} onChange={(days) => setEdit({ ...edit, days })} /></div>
        {!edit.isNew && (
          <DangerButton onClick={() => { set({ windows: q.windows.filter((w) => w.id !== edit.id) }); setEdit(null); }}>
            Zeitfenster löschen
          </DangerButton>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open title="Ruhezeiten" onCancel={onClose} wide>
      <div className="flex items-center justify-between px-1">
        <span className="text-base font-semibold">{q.enabled ? "Ruhezeiten sind an" : "Ruhezeiten sind aus"}</span>
        <Toggle on={q.enabled} onChange={(enabled) => set({ enabled })} label="Ruhezeiten" />
      </div>
      <p className="-mt-2 px-1 text-sm" style={{ color: DS.muted }}>
        In diesen Zeiten macht das Gerät keine Töne und zeigt keine Erinnerungen. Anrufe und Wecker kommen immer durch.
      </p>
      <div className={`flex flex-col gap-5 transition-opacity ${q.enabled ? "" : "pointer-events-none opacity-45"}`}>
        <div className="rounded-3xl p-4 shadow-sm" style={{ background: "#fff" }}>
          <div className="mb-4 flex justify-between">
            {DAY_LABELS.map((label, i) => (
              <button key={label} type="button" onClick={() => setDay(i)} className="h-10 w-10 rounded-full text-sm font-semibold"
                style={day === i ? { background: DS.ink, color: "#fff" } : { color: DS.ink }}>
                {label}
              </button>
            ))}
          </div>
          <DayRing windows={q.windows} day={day} now={now} />
          <div className="mt-4 flex flex-col gap-2">
            {dayWindows.map((w) => (
              <button key={w.id} type="button" onClick={() => setEdit(w)} className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left" style={{ background: DS.field }}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: DS.night }} />
                <span className="flex-1 text-[17px] font-semibold">{w.from} – {w.to}</span>
                <span className="text-sm" style={{ color: DS.muted }}>{formatDays(w.days)}</span>
              </button>
            ))}
            {!dayWindows.length && <div className="p-2 text-center text-sm" style={{ color: DS.muted }}>An diesem Tag keine Ruhezeit.</div>}
            <button type="button" onClick={() => setEdit({ id: newId(), from: "22:00", to: "06:30", days: [day], isNew: true })}
              className="p-2 text-[15px] font-semibold" style={{ color: DS.accent }}>
              + Zeitfenster hinzufügen
            </button>
          </div>
        </div>
        <Label>Bildschirm während der Ruhe</Label>
        <div className="grid grid-cols-2 gap-3">
          {[["black", "Ganz schwarz", "Nichts leuchtet"], ["dim", "Gedimmte Uhr", "Uhrzeit ganz schwach"]].map(([mode, title, sub]) => (
            <SelectTile key={mode} selected={q.mode === mode} onClick={() => set({ mode })}>
              <RoundPreview mode={mode} size={92} now={now} />
              <span className="text-[15px] font-semibold">{title}</span>
              <span className="text-xs" style={{ color: DS.muted }}>{sub}</span>
            </SelectTile>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function SelectTile({ selected, onClick, children }) {
  return (
    <button type="button" aria-pressed={selected} onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-[20px] p-4 text-center shadow-sm"
      style={{ border: `2px solid ${selected ? DS.accent : "transparent"}`, background: selected ? DS.selected : "#fff" }}>
      {children}
    </button>
  );
}

// ─── Wecker ─────────────────────────────────────────────────────────
function AlarmsDialog({ settings, update, onClose }) {
  const alarms = settings.alarms;
  const now = useMinuteClock();
  const [edit, setEdit] = useState(null);
  const [playing, toggle] = useSoundPreview();
  const setAlarms = (next) => update((s) => ({ ...s, alarms: next }));
  const next = nextAlarm(alarms, now);

  if (edit) {
    const save = () => {
      const { isNew, ...alarm } = edit;
      setAlarms(alarms.some((a) => a.id === alarm.id) ? alarms.map((a) => (a.id === alarm.id ? alarm : a)) : [...alarms, { ...alarm, on: true }]);
      setEdit(null);
    };
    return (
      <Dialog open title={edit.isNew ? "Neuer Wecker" : "Wecker bearbeiten"} onCancel={() => setEdit(null)} onSave={save} wide>
        <div className="rounded-[26px] p-4" style={{ background: DS.night }}>
          <TimeInput big dark value={edit.time} onChange={(time) => setEdit({ ...edit, time })} />
        </div>
        <div className="rounded-3xl bg-white p-4 shadow-sm">
          <Label className="mb-3 !px-0">Wiederholen · {formatDays(edit.days)}</Label>
          <DayChips days={edit.days} onChange={(days) => setEdit({ ...edit, days })} />
        </div>
        <div>
          <Label className="mb-2.5">Weckton · tippen zum Anhören</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ALARM_SOUNDS.map((s) => {
              const selected = edit.sound === s.id;
              return (
                <div key={s.id} role="button" tabIndex={0} aria-pressed={selected}
                  onClick={() => setEdit({ ...edit, sound: s.id })}
                  onKeyDown={(e) => e.key === "Enter" && setEdit({ ...edit, sound: s.id })}
                  className="flex cursor-pointer flex-col gap-2.5 rounded-2xl p-3"
                  style={{ border: `2px solid ${selected ? DS.accent : DS.line}`, background: selected ? DS.selected : "#fff" }}>
                  <PlayButton size={36} playing={playing === s.id} onClick={() => toggle(s.id)} />
                  <span className="text-sm font-semibold leading-tight">{s.name}</span>
                </div>
              );
            })}
          </div>
        </div>
        {!edit.isNew && (
          <DangerButton onClick={() => { setAlarms(alarms.filter((a) => a.id !== edit.id)); setEdit(null); }}>Wecker löschen</DangerButton>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog open title="Wecker" onCancel={onClose}>
      <div className="rounded-[26px] px-6 pb-6 pt-5" style={{ background: DS.night, color: DS.nightInk }}>
        <div className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: DS.nightFaint }}>Nächster Wecker</div>
        {next ? (
          <>
            <div style={{ fontFamily: SERIF, fontSize: 64, fontWeight: 500, letterSpacing: -1.5, lineHeight: 1.05 }} className="mt-1.5">{next.alarm.time}</div>
            <div className="mt-1 text-[15px]" style={{ color: DS.nightMuted }}>
              in {Math.floor(next.minutes / 60)} Std. {next.minutes % 60} Min. · {soundName(next.alarm.sound)}
            </div>
          </>
        ) : <div className="mt-2" style={{ fontFamily: SERIF, fontSize: 30 }}>Kein Wecker aktiv</div>}
      </div>
      {alarms.map((a) => (
        <div key={a.id} role="button" tabIndex={0} onClick={() => setEdit(a)} onKeyDown={(e) => e.key === "Enter" && setEdit(a)}
          className="flex cursor-pointer items-center gap-3 rounded-3xl bg-white px-5 py-3.5 shadow-sm">
          <div className="flex-1" style={{ opacity: a.on ? 1 : 0.5 }}>
            <div style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 500, letterSpacing: -0.5 }}>{a.time}</div>
            <div className="text-sm" style={{ color: DS.muted }}>{formatDays(a.days)} · {soundName(a.sound)}</div>
          </div>
          <Toggle on={a.on} label={`Wecker ${a.time}`} onChange={(on) => setAlarms(alarms.map((x) => (x.id === a.id ? { ...x, on } : x)))} />
        </div>
      ))}
      <AddButton onClick={() => setEdit({ id: newId(), time: "08:00", days: [0, 1, 2, 3, 4], sound: "glocken", on: true, isNew: true })}>
        Wecker hinzufügen
      </AddButton>
      <p className="text-center text-[13px]" style={{ color: DS.muted }}>
        Der Wecker klingelt 30 Sekunden lang, auch während einer Ruhezeit. Danach schläft das Gerät weiter.
      </p>
    </Dialog>
  );
}

// ─── Standby-Bildschirm ─────────────────────────────────────────────
// Design variant B: a dark card with the live preview and the chosen option's
// own setting, then four tiles. No text in the card (the selected tile names
// the option); clock and "Bildschirm aus" have a setting under the preview, and
// for the animation and photos the preview takes that room too, so the card
// keeps its height.
const EXTRA_HEIGHT = 84;
const PREVIEW_SIZE = 274; // 230 in the design, plus the room its title took
const PREVIEW_AREA_FULL = PREVIEW_SIZE + EXTRA_HEIGHT + 16;

function StandbyDialog({ settings, update, onClose }) {
  const s = settings.standby;
  const now = useMinuteClock();
  const set = (patch) => update((all) => ({ ...all, standby: { ...all.standby, ...patch } }));
  const current = STANDBY_OPTIONS.find((o) => o.id === s.type);
  const previewArea = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(PREVIEW_AREA_FULL);
  useEffect(() => {
    const el = previewArea.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setPreviewWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  let extra = null;
  if (s.type === "clock") {
    extra = (
      <div role="radiogroup" aria-label="Zifferblatt" className="flex gap-1.5 rounded-full p-1" style={{ background: "rgba(255,255,255,0.08)" }}>
        {[["antik", "Antik"], ["modern", "Modern"]].map(([face, label]) => (
          <button key={face} type="button" role="radio" aria-checked={s.face === face} onClick={() => set({ face })}
            className="rounded-full px-[22px] py-2.5 text-[15px] font-semibold"
            style={s.face === face ? { background: DS.nightInk, color: DS.ink } : { color: DS.nightInk }}>
            {label}
          </button>
        ))}
      </div>
    );
  } else if (s.type === "off") {
    extra = <OffSlider value={s.off_after_s} onChange={(off_after_s) => set({ off_after_s })} />;
  }

  return (
    <Dialog open title="Standby-Bildschirm" onCancel={onClose}>
      <style>{"@keyframes dsFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}"}</style>
      <div className="flex flex-col items-center gap-4 rounded-[28px] px-5 pb-[22px] pt-7" style={{ background: DS.night }}>
        <div ref={previewArea} aria-label={`Vorschau: ${current.name}`} role="img"
          className="flex items-center justify-center self-stretch" style={extra ? undefined : { height: PREVIEW_AREA_FULL }}>
          <RoundPreview mode={s.type} face={s.face} offAfter={s.off_after_s}
            size={Math.min(extra ? PREVIEW_SIZE : PREVIEW_AREA_FULL, previewWidth)} now={now} />
        </div>
        {extra && (
          <div className="flex items-center justify-center self-stretch" style={{ height: EXTRA_HEIGHT }}>
            <div key={s.type} className="flex w-full justify-center" style={{ animation: "dsFade 220ms ease both" }}>{extra}</div>
          </div>
        )}
      </div>
      <Label>Auswählen</Label>
      <div className="grid grid-cols-2 gap-2.5">
        {STANDBY_OPTIONS.map((o) => (
          <SelectTile key={o.id} selected={s.type === o.id} onClick={() => set({ type: o.id })}>
            <RoundPreview mode={o.id} face={s.face} offAfter={s.off_after_s} size={80} now={now} />
            <span className="text-sm font-semibold">{o.name}</span>
          </SelectTile>
        ))}
      </div>
    </Dialog>
  );
}

/** "Bildschirm aus nach" on the dark card; snaps to OFF_AFTER_STEPS. */
function OffSlider({ value, onChange }) {
  const last = OFF_AFTER_STEPS.length - 1;
  const index = Math.max(0, OFF_AFTER_STEPS.indexOf(value));
  return (
    // Inset so the thumb at 5 Sek. / 5 Min. stays clear of the card's edge.
    <div className="w-full px-4">
      <style>{`.ds-range{-webkit-appearance:none;appearance:none;height:6px;border-radius:3px;outline:none;background:linear-gradient(90deg,${DS.accent} var(--p),${DS.track} var(--p))}
.ds-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:28px;height:28px;border-radius:50%;background:#fff;border:3px solid ${DS.accent};cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.2)}
.ds-range::-moz-range-thumb{width:24px;height:24px;border-radius:50%;background:#fff;border:3px solid ${DS.accent};cursor:pointer}`}</style>
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[15px]" style={{ color: DS.nightMuted }}>Bildschirm aus nach</span>
        <span style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 500, color: DS.nightInk }}>{formatSeconds(value)}</span>
      </div>
      <input type="range" min="0" max={last} step="1" value={index} aria-label="Bildschirm aus nach"
        aria-valuetext={formatSeconds(value)} onChange={(e) => onChange(OFF_AFTER_STEPS[+e.target.value])}
        className="ds-range w-full" style={{ "--p": `${(index / last) * 100}%` }} />
      <div className="mt-2 flex justify-between text-xs" style={{ color: DS.nightFaint }}>
        <span>{formatSeconds(OFF_AFTER_STEPS[0])}</span><span>{formatSeconds(OFF_AFTER_STEPS[last])}</span>
      </div>
    </div>
  );
}
